import React, { useMemo, useState, useEffect } from 'react';
import { ACCENT_ORANGE, ACCENT_PURPLE, ACCENT_GREEN } from '../styles/shared';

// ─── Badge definitions ─────────────────────────────────────────
const BADGES = [
  { emoji: '\u{1F331}', name: 'First Entry', threshold: 1 },
  { emoji: '\u270D\uFE0F', name: 'Getting Started', threshold: 5 },
  { emoji: '\u{1F4DD}', name: 'Consistent Writer', threshold: 10 },
  { emoji: '\u{1F4D6}', name: 'Storyteller', threshold: 25 },
  { emoji: '\u{1F3C6}', name: 'Achievement Hunter', threshold: 50 },
  { emoji: '\u2B50', name: 'BragSheet Pro', threshold: 100 },
];

const DEFAULT_WEEKLY_GOAL = 3;

// ─── Helpers ────────────────────────────────────────────────────
// Weekly streak math — must match src/api/lib/streak.js (single source of
// truth for the rule "current week never breaks the streak; ≥1 entry per
// Mon-Sun week extends it"). Kept inline to avoid pulling backend code into
// the React bundle; the backend version drives the email Lambda.

function entryDateStr(e) {
  if (!e) return null;
  if (e.entryDate) return String(e.entryDate).slice(0, 10);
  if (e.createdAt) return String(e.createdAt).slice(0, 10);
  return null;
}

function dateToMondayKey(yyyyMmDd) {
  const d = new Date(yyyyMmDd + 'T00:00:00Z');
  const dow = d.getUTCDay(); // 0=Sun, 1=Mon, …
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  d.setUTCDate(d.getUTCDate() - daysFromMonday);
  return d.toISOString().slice(0, 10);
}

function shiftWeekKey(weekKey, weeks) {
  const d = new Date(weekKey + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

function weekKeysWithEntries(entries) {
  const set = new Set();
  for (const e of entries || []) {
    const d = entryDateStr(e);
    if (d) set.add(dateToMondayKey(d));
  }
  return set;
}

function thisWeekKey(now) {
  return dateToMondayKey(now.toISOString().slice(0, 10));
}

function calcWeeklyStreak(entries, now = new Date()) {
  const weeks = weekKeysWithEntries(entries);
  const thisWk = thisWeekKey(now);
  let cursor;
  if (weeks.has(thisWk)) cursor = thisWk;
  else {
    const lastWk = shiftWeekKey(thisWk, -1);
    if (!weeks.has(lastWk)) return 0;
    cursor = lastWk;
  }
  let streak = 0;
  while (weeks.has(cursor)) { streak++; cursor = shiftWeekKey(cursor, -1); }
  return streak;
}

function buildWeekGrid(entries, now = new Date(), count = 7) {
  const weeks = weekKeysWithEntries(entries);
  const thisWk = thisWeekKey(now);
  const cells = [];
  for (let offset = count - 1; offset >= 0; offset--) {
    const key = shiftWeekKey(thisWk, -offset);
    let status, label;
    if (offset === 0) { status = weeks.has(key) ? 'filled' : 'pending'; label = 'This'; }
    else if (offset === 1) { status = weeks.has(key) ? 'filled' : 'empty'; label = 'Last'; }
    else { status = weeks.has(key) ? 'filled' : 'empty'; label = `${offset}w`; }
    cells.push({ weekKey: key, status, label });
  }
  return cells;
}

function calcWeeklyEntries(entries) {
  if (!entries || entries.length === 0) return 0;
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);

  return entries.filter(e => {
    const d = new Date(e.entryDate || e.createdAt);
    return d >= startOfWeek;
  }).length;
}

// ─── CSS keyframes (injected once) ─────────────────────────────
const STYLE_ID = 'streak-badges-keyframes';
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes streak-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.15); }
    }
    @keyframes badge-pop {
      0% { transform: scale(0.8); opacity: 0.5; }
      60% { transform: scale(1.1); }
      100% { transform: scale(1); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

// ─── Styles ─────────────────────────────────────────────────────
const s = {
  wrapper: (isMobile) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr',
    gap: '1rem',
    marginBottom: '2rem',
  }),

  // Streak card
  streakCard: {
    background: `linear-gradient(135deg, ${ACCENT_ORANGE}, #ea580c)`,
    borderRadius: '16px',
    padding: '1.5rem',
    color: '#ffffff',
    textAlign: 'center',
    boxShadow: '0 4px 14px rgba(249,115,22,0.3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '140px',
  },
  streakEmoji: (active) => ({
    fontSize: '2.5rem',
    lineHeight: 1,
    marginBottom: '0.4rem',
    animation: active ? 'streak-pulse 2s ease-in-out infinite' : 'none',
    display: 'inline-block',
  }),
  streakNumber: {
    fontSize: '2rem',
    fontWeight: '800',
    lineHeight: 1.1,
  },
  streakLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    opacity: 0.9,
    marginTop: '0.25rem',
  },
  gridRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%',
    marginTop: '0.85rem',
    gap: '0.2rem',
  },
  gridCellWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1 1 0',
    minWidth: 0,
  },
  gridLabel: (isThis) => ({
    fontSize: '0.6rem',
    color: isThis ? '#fff' : 'rgba(255,255,255,0.7)',
    fontWeight: isThis ? '700' : '500',
    marginBottom: '4px',
    letterSpacing: '0.02em',
  }),
  gridCircle: (status) => {
    const base = {
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '11px',
      fontWeight: '700',
      lineHeight: 1,
    };
    if (status === 'filled') return { ...base, backgroundColor: '#ffffff', color: ACCENT_ORANGE };
    if (status === 'pending') return { ...base, backgroundColor: 'transparent', border: '2px dashed #ffffff' };
    return { ...base, backgroundColor: 'rgba(255,255,255,0.25)' };
  },

  // Badges card
  badgesCard: {
    background: `linear-gradient(135deg, ${ACCENT_PURPLE}12, ${ACCENT_PURPLE}08)`,
    border: `1px solid ${ACCENT_PURPLE}30`,
    borderRadius: '16px',
    padding: '1.25rem',
    boxShadow: '0 2px 8px rgba(139,92,246,0.08)',
  },
  badgesTitle: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: ACCENT_PURPLE,
    marginBottom: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badgesRow: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: '0.75rem',
  },
  badgeItem: (earned) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '64px',
    animation: earned ? 'badge-pop 0.4s ease-out' : 'none',
  }),
  // T21: Three visual states for milestone badges:
  //   - earned: white fill, purple border, full color, popped in
  //   - next-up (the first unachieved milestone): teal-tinted background and
  //     teal border to signal "this one's within reach"
  //   - future (every milestone after next-up): the previous gray treatment
  badgeCircle: (state) => {
    const base = {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.3rem',
      position: 'relative',
    };
    if (state === 'earned') {
      return {
        ...base,
        backgroundColor: '#ffffff',
        border: `2px solid ${ACCENT_PURPLE}`,
        filter: 'none',
        opacity: 1,
        boxShadow: '0 2px 6px rgba(139,92,246,0.2)',
      };
    }
    if (state === 'next') {
      return {
        ...base,
        // Subtle teal-tinted background — still visibly unachieved (no purple,
        // no animation) but clearly distinguished from the gray "future" state.
        backgroundColor: '#e0f2f1',
        border: '2px solid #01696F',
        filter: 'grayscale(0.4)',
        opacity: 0.85,
        boxShadow: '0 2px 6px rgba(1,105,111,0.18)',
      };
    }
    // future
    return {
      ...base,
      backgroundColor: '#e2e8f0',
      border: '2px solid #cbd5e1',
      filter: 'grayscale(1)',
      opacity: 0.5,
    };
  },
  badgeLock: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    fontSize: '0.6rem',
    backgroundColor: '#94a3b8',
    borderRadius: '50%',
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
  },
  badgeName: (state) => ({
    fontSize: '0.65rem',
    color: state === 'earned' ? '#475569' : state === 'next' ? '#01696F' : '#94a3b8',
    marginTop: '0.25rem',
    textAlign: 'center',
    fontWeight: state === 'earned' || state === 'next' ? '600' : '400',
    lineHeight: 1.2,
  }),
  progressBarTrack: {
    height: '6px',
    backgroundColor: '#e2e8f0',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBarFill: (pct) => ({
    height: '100%',
    width: `${Math.min(100, pct)}%`,
    backgroundColor: ACCENT_PURPLE,
    borderRadius: '3px',
    transition: 'width 0.6s ease-out',
  }),
  progressLabel: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '0.35rem',
    textAlign: 'center',
  },

  // Weekly goal card
  goalCard: {
    background: `linear-gradient(135deg, ${ACCENT_GREEN}12, ${ACCENT_GREEN}08)`,
    border: `1px solid ${ACCENT_GREEN}30`,
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(34,197,94,0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minHeight: '140px',
  },
  goalTitle: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: ACCENT_GREEN,
    marginBottom: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  goalRing: {
    position: 'relative',
    width: '80px',
    height: '80px',
    marginBottom: '0.5rem',
  },
  goalCenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '0.85rem',
    fontWeight: '700',
    color: '#1e293b',
  },
  goalMessage: {
    fontSize: '0.8rem',
    fontWeight: '600',
    marginTop: '0.25rem',
  },
};

// ─── Progress ring SVG ──────────────────────────────────────────
function ProgressRing({ current, goal }) {
  const pct = Math.min(1, current / goal);
  const r = 34;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct);
  const complete = current >= goal;

  return (
    <div style={s.goalRing}>
      <svg width="80" height="80" viewBox="0 0 80 80" aria-hidden="true">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
        <circle
          cx="40" cy="40" r={r} fill="none"
          stroke={complete ? ACCENT_GREEN : '#86efac'}
          strokeWidth="7"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
          style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
        />
      </svg>
      <div style={s.goalCenter}>{current}/{goal}</div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────
// T20: `weeklyGoal` is user-editable (persisted server-side on the user record).
// Falls back to DEFAULT_WEEKLY_GOAL when the user hasn't set one. `onEditGoal`
// is invoked with the new integer when the user saves a change; the parent is
// responsible for persisting via PUT /api/profile and re-passing the new value.
export default function StreakAndBadges({ entries, isMobile, weeklyGoal, onEditGoal }) {
  ensureKeyframes();

  const goal = Number.isInteger(weeklyGoal) && weeklyGoal >= 1 && weeklyGoal <= 10
    ? weeklyGoal
    : DEFAULT_WEEKLY_GOAL;

  const streak = useMemo(() => calcWeeklyStreak(entries), [entries]);
  const weekGrid = useMemo(() => buildWeekGrid(entries, new Date(), 7), [entries]);
  const totalEntries = entries ? entries.length : 0;
  const weeklyEntries = useMemo(() => calcWeeklyEntries(entries), [entries]);
  const goalMet = weeklyEntries >= goal;

  // Local editor state for the inline "Set goal" affordance.
  const [editing, setEditing] = useState(false);
  const [draftGoal, setDraftGoal] = useState(goal);
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalError, setGoalError] = useState('');

  // Keep the draft in sync if the parent passes a new goal (e.g. after a refresh).
  useEffect(() => { setDraftGoal(goal); }, [goal]);

  const handleSaveGoal = async () => {
    const n = parseInt(draftGoal, 10);
    if (isNaN(n) || n < 1 || n > 10) {
      setGoalError('Pick a number from 1 to 10.');
      return;
    }
    if (!onEditGoal) { setEditing(false); return; }
    setSavingGoal(true);
    setGoalError('');
    try {
      await onEditGoal(n);
      setEditing(false);
    } catch (e) {
      setGoalError('Could not save. Try again.');
    } finally {
      setSavingGoal(false);
    }
  };

  // Next badge progress
  const nextBadge = BADGES.find(b => totalEntries < b.threshold);
  const prevThreshold = BADGES.filter(b => totalEntries >= b.threshold).slice(-1)[0]?.threshold || 0;
  const nextThreshold = nextBadge ? nextBadge.threshold : BADGES[BADGES.length - 1].threshold;
  const progressPct = nextBadge
    ? ((totalEntries - prevThreshold) / (nextThreshold - prevThreshold)) * 100
    : 100;

  const streakLabel = streak === 0
    ? 'Start your streak this week!'
    : streak === 1
    ? 'week streak! Keep it going!'
    : `week streak! You're on a roll!`;

  return (
    <div style={s.wrapper(isMobile)}>
      {/* Streak (weekly, with 7-week rolling grid) */}
      <div style={s.streakCard} role="status" aria-label={`Writing streak: ${streak} weeks`}>
        <span style={s.streakEmoji(streak > 0)} role="img" aria-hidden="true">
          {streak > 0 ? '\u{1F525}' : '\u{1F9CA}'}
        </span>
        <div style={s.streakNumber}>{streak}</div>
        <div style={s.streakLabel}>{streakLabel}</div>

        {/* 7-week rolling grid: matches the email template visualization */}
        <div style={s.gridRow} aria-hidden="true">
          {weekGrid.map((w) => (
            <div key={w.weekKey} style={s.gridCellWrap}>
              <div style={s.gridLabel(w.status === 'pending')}>{w.label}</div>
              <div style={s.gridCircle(w.status)}>
                {w.status === 'filled' ? '✓' : ''}
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Badges */}
      <div style={s.badgesCard}>
        <div style={s.badgesTitle}>Entry Milestones</div>
        <div style={s.badgesRow}>
          {BADGES.map((badge) => {
            const earned = totalEntries >= badge.threshold;
            // T21: "next up" = the single first unachieved milestone in the
            // ordered BADGES list. Everything after it stays "future" (gray).
            const isNext = !earned && nextBadge && badge.threshold === nextBadge.threshold;
            const state = earned ? 'earned' : isNext ? 'next' : 'future';
            const titleSuffix = state === 'next' ? ' — next up' : '';
            return (
              <div key={badge.threshold} style={s.badgeItem(earned)} title={`${badge.name} — ${badge.threshold} entries${titleSuffix}`}>
                <div style={s.badgeCircle(state)}>
                  {badge.emoji}
                  {!earned && <span style={s.badgeLock}>{'\u{1F512}'}</span>}
                </div>
                <span style={s.badgeName(state)}>{badge.name}</span>
              </div>
            );
          })}
        </div>
        <div style={s.progressBarTrack}>
          <div style={s.progressBarFill(progressPct)} />
        </div>
        <div style={s.progressLabel}>
          {nextBadge
            ? `${totalEntries}/${nextBadge.threshold} toward "${nextBadge.name}"`
            : 'All badges unlocked! Amazing!'}
        </div>
      </div>

      {/* Weekly goal — T20: tooltip via title attr + inline editor */}
      <div
        style={s.goalCard}
        role="status"
        aria-label={`Weekly goal: ${weeklyEntries} of ${goal}`}
        tabIndex={0}
        title={`Your weekly goal: ${goal} journal ${goal === 1 ? 'entry' : 'entries'}. Updated weekly on Sundays.`}
      >
        <div style={s.goalTitle}>Weekly Goal</div>
        <ProgressRing current={weeklyEntries} goal={goal} />
        <div style={{
          ...s.goalMessage,
          color: goalMet ? ACCENT_GREEN : '#64748b',
        }}>
          {goalMet
            ? '\u{1F389} Weekly goal complete!'
            : `${goal - weeklyEntries} more to go this week`}
        </div>

        {editing ? (
          <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <label htmlFor="weekly-goal-input" style={{ fontSize: '0.75rem', color: '#475569' }}>Goal:</label>
            <input
              id="weekly-goal-input"
              type="number"
              min={1}
              max={10}
              value={draftGoal}
              onChange={e => setDraftGoal(e.target.value)}
              aria-label="Weekly journal entry goal (1-10)"
              style={{
                width: '56px', padding: '0.25rem 0.4rem', borderRadius: '6px',
                border: '1px solid #cbd5e1', fontSize: '0.8rem', textAlign: 'center',
              }}
            />
            <button
              type="button"
              onClick={handleSaveGoal}
              disabled={savingGoal}
              style={{
                padding: '0.3rem 0.6rem', borderRadius: '6px', border: 'none',
                backgroundColor: ACCENT_GREEN, color: '#fff', fontSize: '0.75rem',
                fontWeight: '600', cursor: savingGoal ? 'not-allowed' : 'pointer',
                opacity: savingGoal ? 0.6 : 1,
              }}
            >
              {savingGoal ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setDraftGoal(goal); setGoalError(''); }}
              style={{
                padding: '0.3rem 0.6rem', borderRadius: '6px',
                border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569',
                fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            {goalError && (
              <div role="alert" style={{ width: '100%', color: '#ef4444', fontSize: '0.72rem', marginTop: '0.25rem' }}>{goalError}</div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit weekly goal"
            style={{
              marginTop: '0.5rem', background: 'none', border: 'none', padding: '0.2rem 0.4rem',
              color: ACCENT_GREEN, fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            ✎ Set goal
          </button>
        )}
      </div>
    </div>
  );
}
